import { useMany, useTranslate } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { Card, Progress, Table } from 'antd';
import type {
  PublicationListOut,
  PublicationProgressionListOut,
} from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash } from '@/components/common/show/shared';
import {
  ConsumptionStatusTag,
  ProductFormatTag,
} from '@/components/progress/tags';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { formatDateTime } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';

export const ProfileProgressionsTab = ({
  profileId,
}: {
  profileId: number;
}) => {
  const t = useTranslate();
  const { tableProps, sorters, result } =
    useTable<PublicationProgressionListOut>({
      resource: 'progress.progressions',
      queryOptions: { enabled: profileId != null },
      filters: {
        permanent: [{ field: 'profile_id', operator: 'eq', value: profileId }],
      },
      sorters: { initial: [{ field: 'updated_at', order: 'desc' }] },
      pagination: { pageSize: 10 },
      syncWithLocation: false,
    });

  const publicationIds = [
    ...new Set(
      (result?.data ?? []).map((r) => r.publication_id).filter(Boolean)
    ),
  ];
  const { result: publicationsResult } = useMany<PublicationListOut>({
    resource: 'user_catalog.publications',
    ids: publicationIds,
    queryOptions: { enabled: publicationIds.length > 0 },
  });
  const publicationMap = new Map<number, PublicationListOut>(
    (publicationsResult?.data ?? []).map((p: PublicationListOut) => [p.id, p])
  );

  return (
    <Card
      title={`${t('account.profiles.tabs.progressions')} (${result?.total ?? 0})`}
    >
      <Table<PublicationProgressionListOut>
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
        size="small"
      >
        <Table.Column<PublicationProgressionListOut>
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowIconLink resource="progress.progressions" id={value}>
              {value}
            </ShowIconLink>
          )}
        />
        <Table.Column<PublicationProgressionListOut>
          dataIndex="publication_id"
          title={t('progress.progressions.fields.publication_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'publication_id')}
          render={(value: number) => (
            <PublicationInfoPopover
              publication={publicationMap.get(value)}
              publicationId={value}
            />
          )}
        />
        <Table.Column<PublicationProgressionListOut>
          dataIndex="consumption_status"
          title={t('progress.progressions.fields.consumption_status')}
          render={(value: string) =>
            orDash(value && <ConsumptionStatusTag status={value} />)
          }
        />
        <Table.Column<PublicationProgressionListOut>
          dataIndex="active_format"
          title={t('progress.progressions.fields.active_format')}
          render={(value: string | null) =>
            orDash(
              value ? (
                <ProductFormatTag value={value} labels="progressions" />
              ) : null
            )
          }
        />
        <Table.Column<PublicationProgressionListOut>
          dataIndex="progress"
          title={t('progress.progressions.fields.progress')}
          sorter
          sortOrder={getSortOrder(sorters, 'progress')}
          render={(value: number) => (
            <div style={{ minWidth: 130 }}>
              <Progress percent={value} size="small" />
            </div>
          )}
        />
        <Table.Column<PublicationProgressionListOut>
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          render={(value: string) => orDash(formatDateTime(value))}
        />
      </Table>
    </Card>
  );
};
